import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { MaxsoldInvoiceDto, MaxsoldInvoicesDto, MaxsoldStatusDto } from '@jordan/types';
import { Alert, Badge, Breadcrumb, Button, DataTable, PageHeader, Spinner, type DataTableColumn } from '@jordan/ui';
import { AppChrome } from '../../components/AppChrome';
import { ApiError, apiGet } from '../../lib/api';
import { maxsoldSidebar } from './maxsoldSidebar';

const money = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatDate = (iso: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

export function MaxsoldInvoicesPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [status, setStatus] = useState<MaxsoldStatusDto | null>(null);
  const [data, setData] = useState<MaxsoldInvoicesDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    apiGet<MaxsoldStatusDto>('/api/maxsold/status')
      .then(setStatus)
      .catch(() => setErr('Failed to load Maxsold status.'));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setErr(null);
    apiGet<MaxsoldInvoicesDto>('/api/maxsold/invoices')
      .then(setData)
      .catch((e: unknown) => {
        const body = e instanceof ApiError ? (e.body as { message?: string } | null) : null;
        setErr(body?.message ?? 'Could not load your invoices.');
        setData(null);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (status?.configured) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.configured]);

  const columns = useMemo<DataTableColumn<MaxsoldInvoiceDto>[]>(
    () => [
      { key: 'created', header: 'Date', sortable: true, sortValue: (i) => Date.parse(i.created) || 0, render: (i) => formatDate(i.created) },
      {
        key: 'auctionTitle',
        header: 'Auction',
        sortable: true,
        render: (i) => (
          <div>
            <div className="fw-semibold">{i.auctionTitle}</div>
            <div className="text-muted fs-sm">Invoice #{i.id}</div>
          </div>
        ),
      },
      { key: 'itemCount', header: 'Items', align: 'center', sortable: true },
      { key: 'invoiceTotal', header: 'Total', align: 'end', sortable: true, render: (i) => money(i.invoiceTotal) },
      {
        key: 'balance',
        header: 'Balance',
        align: 'end',
        sortable: true,
        render: (i) => (i.balance > 0 ? <span className="text-danger fw-semibold">{money(i.balance)}</span> : money(i.balance)),
      },
      {
        key: 'status',
        header: 'Status',
        align: 'center',
        sortValue: (i) => (i.paid ? 1 : 0),
        render: (i) => (
          <span className="d-inline-flex gap-1">
            {i.paid ? <Badge variant="success">Paid</Badge> : <Badge variant="danger">Unpaid</Badge>}
            {i.pickedUp && <Badge variant="info">Picked up</Badge>}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <AppChrome secondarySidebar={maxsoldSidebar(navigate, pathname)}>
      <PageHeader
        title={
          <>
            Maxsold - <span className="fw-normal">Invoices</span>
          </>
        }
        breadcrumb={<Breadcrumb items={[{ icon: 'ph-house', href: '/' }, { label: 'Maxsold' }, { label: 'Invoices' }]} />}
      />

      <div className="content">
        {status && !status.configured && (
          <Alert variant="info" icon="ph-info">
            Maxsold isn&apos;t connected. Add <code>MAXSOLD_EMAIL</code> and <code>MAXSOLD_PASSWORD</code> to the server{' '}
            <code>.env</code> to view your invoices.
          </Alert>
        )}
        {err && (
          <Alert variant="danger" icon="ph-warning" dismissible>
            {err}
          </Alert>
        )}

        {status?.configured && (
          <div className="card">
            <div className="card-header d-flex align-items-center gap-2">
              <h5 className="mb-0 me-auto">
                Invoices{data ? <span className="text-muted fw-normal ms-2">{data.total} invoices</span> : null}
              </h5>
              <div className="form-control-feedback form-control-feedback-start" style={{ maxWidth: 240 }}>
                <input
                  type="search"
                  className="form-control"
                  placeholder="Search…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                <div className="form-control-feedback-icon">
                  <i className="ph-magnifying-glass opacity-50" />
                </div>
              </div>
              <Button variant="light" icon="ph-arrows-clockwise" disabled={loading} onClick={load}>
                Refresh
              </Button>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-5">
                  <Spinner />
                </div>
              ) : (
                <DataTable
                  columns={columns}
                  rows={data?.items ?? []}
                  getRowKey={(i) => String(i.id)}
                  searchQuery={query}
                  pageSize={status?.favouritesPageSize ?? 0}
                  initialSort={{ key: 'created', dir: 'desc' }}
                  emptyMessage="No invoices yet."
                />
              )}
            </div>
          </div>
        )}
      </div>
    </AppChrome>
  );
}
